import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

// ========================
// CONFIGURATION TOKENS
// ========================
const TOKENS = {
  // Vignette radiale (micro1 style)
  vignette: {
    center: '50% 40%',
    stops: [
      'rgba(0, 0, 0, 0.10) 0%',
      'rgba(0, 0, 0, 0.75) 65%',
      'rgba(0, 0, 0, 0.96) 100%',
    ],
  },
  // Top fade (pour nav)
  topFade: {
    opacity: 0.65,
    stops: ['rgba(0, 0, 0, 1) 0%', 'rgba(0, 0, 0, 0) 30%'],
  },
  // Bottom fade
  bottomFade: {
    opacity: 0.85,
    stops: ['rgba(0, 0, 0, 1) 0%', 'rgba(0, 0, 0, 0) 50%'],
  },
  // Talentees emerald tint
  emeraldTint: {
    color: 'rgba(40, 230, 117, 0.03)',
    center: '50% 50%',
    radius: '60%',
  },
  // Noise
  noise: {
    opacity: 0.06,
  },
  // Color normalizer (pour uniformiser les images)
  colorNormalizer: {
    brightness: 0.95,
    contrast: 1.05,
    saturate: 0.92,
  },
};

// Grade presets pour ajuster légèrement selon la section
const GRADE_PRESETS = {
  hero: {
    brightness: 1.0,
    contrast: 1.0,
    saturate: 1.0,
  },
  narrative: {
    brightness: 0.98,
    contrast: 1.02,
    saturate: 0.95,
  },
};

// ========================
// PROPS
// ========================
interface BackgroundTreatmentProps {
  imageSrc: string;
  imageAlt?: string;
  grade?: 'hero' | 'narrative';
  objectPosition?: string;
  enableKenBurns?: boolean;
  className?: string;
}

// ========================
// COMPONENT
// ========================
export const BackgroundTreatment: React.FC<BackgroundTreatmentProps> = ({
  imageSrc,
  imageAlt = 'Background',
  grade = 'hero',
  objectPosition = 'center center',
  enableKenBurns = true,
  className = '',
}) => {
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // Get grade-specific adjustments
  const gradeAdjust = GRADE_PRESETS[grade];

  // Compute final filter
  const imageFilter = `
    brightness(${TOKENS.colorNormalizer.brightness * gradeAdjust.brightness})
    contrast(${TOKENS.colorNormalizer.contrast * gradeAdjust.contrast})
    saturate(${TOKENS.colorNormalizer.saturate * gradeAdjust.saturate})
  `.trim();

  return (
    <div className={`absolute inset-0 w-full h-full overflow-hidden ${className}`}>
      {/* ========================
          LAYER 0: Base Image with Ken Burns + Color Normalizer
          ======================== */}
      <motion.div
        className="absolute inset-0 w-full h-full"
        initial={{ scale: 1.02 }}
        animate={
          enableKenBurns && !prefersReducedMotion
            ? { scale: 1.08, y: '-1%' }
            : { scale: 1.02 }
        }
        transition={{
          duration: 25,
          ease: 'linear',
          repeat: Infinity,
          repeatType: 'reverse',
        }}
        style={{ filter: imageFilter }}
      >
        <img
          src={imageSrc}
          alt={imageAlt}
          className="w-full h-full object-cover"
          style={{
            objectPosition,
            willChange: 'transform',
          }}
          loading="eager"
        />
      </motion.div>

      {/* ========================
          LAYER 1: Radial Vignette (micro1 style)
          ======================== */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(
            circle at ${TOKENS.vignette.center},
            ${TOKENS.vignette.stops.join(', ')}
          )`,
        }}
      />

      {/* ========================
          LAYER 2: Top Fade (for nav readability)
          ======================== */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(to bottom, ${TOKENS.topFade.stops.join(', ')})`,
          opacity: TOKENS.topFade.opacity,
        }}
      />

      {/* ========================
          LAYER 3: Bottom Fade
          ======================== */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(to top, ${TOKENS.bottomFade.stops.join(', ')})`,
          opacity: TOKENS.bottomFade.opacity,
        }}
      />

      {/* ========================
          LAYER 4: Talentees Emerald Tint
          ======================== */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(
            circle at ${TOKENS.emeraldTint.center},
            ${TOKENS.emeraldTint.color} 0%,
            rgba(40, 230, 117, 0) ${TOKENS.emeraldTint.radius}
          )`,
        }}
      />

      {/* ========================
          LAYER 5: Noise Texture Overlay
          ======================== */}
      <div
        className="absolute inset-0 pointer-events-none mix-blend-overlay"
        style={{
          opacity: TOKENS.noise.opacity,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '200px 200px',
        }}
      />

      {/* ========================
          LAYER 6: Color Unifier (subtle dark overlay for consistency)
          ======================== */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          mixBlendMode: 'multiply',
        }}
      />
    </div>
  );
};

// ========================
// SEAM BRIDGE COMPONENT
// For smooth transition between sections
// ========================
interface SeamBridgeProps {
  position: 'top' | 'bottom';
  height?: number;
}

export const SeamBridge: React.FC<SeamBridgeProps> = ({
  position,
  height = 120,
}) => {
  const isTop = position === 'top';

  return (
    <div
      className="absolute left-0 right-0 pointer-events-none"
      style={{
        [isTop ? 'top' : 'bottom']: 0,
        height: `${height}px`,
        background: isTop
          ? 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)'
          : 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
        zIndex: 5,
      }}
    />
  );
};

export default BackgroundTreatment;
